import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  Alert,
  Divider,
  Container,
  Paper,
  Fade,
  Slide,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Security as SecurityIcon,
  AccountCircle as AccountIcon,
  VpnKey as KeyIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { LoaderWrapper } from './index';

// Validation schemas for each step
const step1Schema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format')
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Please enter a valid email address'
    )
    .max(100, 'Email must not exceed 100 characters'),
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must not exceed 50 characters')
    .trim(),
  mobile: yup
    .string()
    .required('Mobile number is required')
    .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits')
    .test('valid-indian-mobile', 'Please enter a valid Indian mobile number', (value) => {
      if (!value) return false;
      // Indian mobile numbers start with 6, 7, 8, or 9
      return /^[6-9][0-9]{9}$/.test(value);
    }),
});

const step2Schema = yup.object({
  otp: yup.string()
    .matches(/^[0-9]{6}$/, 'OTP must be 6 digits')
    .required('OTP is required'),
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orgId, setOrgId] = useState('');
  const [sentOtp, setSentOtp] = useState('');

  const steps = ['Enter Details', 'Verify OTP'];

  // Form for Step 1 (Enter Details)
  const {
    control: step1Control,
    handleSubmit: handleStep1Submit,
    formState: { errors: step1Errors },
    getValues: getStep1Values,
  } = useForm({
    resolver: yupResolver(step1Schema),
    defaultValues: {
      email: '',
      username: '',
      mobile: '',
    },
  });

  // Form for Step 2 (Verify OTP)
  const {
    control: step2Control,
    handleSubmit: handleStep2Submit,
    formState: { errors: step2Errors },
  } = useForm({
    resolver: yupResolver(step2Schema),
    defaultValues: {
      otp: '',
    },
  });

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Step 1: Send OTP
  const onSendOTP = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/forgot-password-send-otp`,
        {
          ContactEmail: data.email.trim(),
          LoginName: data.username.trim(),
          mobile: data.mobile.trim(),
        }
      );

      if (response.data.success) {
        setOrgId(response.data.data.orgId);
        setSentOtp(response.data.data.otp); // For debugging only, remove in production
        setSuccess('OTP has been sent to your Registered Email Id');
        toast.success('OTP has been sent to your registered Email Id');
        setActiveStep(1);
      } else {
        setError(response.data.message || 'Failed to send OTP');
        toast.error(response.data.message || 'Failed to send OTP');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      const errorMessage = err.response?.data?.message || 'Email or UserId or Mobile No does not Exist.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const onResendOTP = async () => {
    const values = getStep1Values();
    await onSendOTP(values);
  };

  // Step 2: Verify OTP
  const onVerifyOTP = async (data) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/forgot-password-verify-otp`,
        {
          otp: data.otp.trim(),
          orgId: orgId,
        }
      );

      if (response.data.success) {
        toast.success('OTP has been Verified Successfully.');
        // Redirect to reset password page with encrypted orgId
        setTimeout(() => {
          navigate(`/main/reset-password?token=${response.data.data.resetToken}`);
        }, 1000);
      } else {
        setError(response.data.message || 'OTP verification failed');
        toast.error(response.data.message || 'OTP has not been Verified Successfully.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      const errorMessage = err.response?.data?.message || 'OTP has not been Verified Successfully.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 8,
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #667eea 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          animation: 'float 20s ease-in-out infinite',
        },
      }}
    >
      <Container maxWidth="sm">
        <Fade in={mounted} timeout={1000}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              transform: mounted ? 'translateY(0)' : 'translateY(50px)',
              transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                color: 'white',
                p: 4,
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #06b6d4)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s ease-in-out infinite',
                },
              }}
            >
              <Slide direction="down" in={mounted} timeout={800}>
                <Box>
                  <KeyIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography
                    variant="h4"
                    component="h1"
                    fontWeight="bold"
                    sx={{
                      mb: 1,
                      background: 'linear-gradient(45deg, #ffffff, #e0f2fe)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    Forgot Password
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Reset your NABL Portal password
                  </Typography>
                </Box>
              </Slide>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <Slide direction="up" in={mounted} timeout={1000}>
                <Box>
                  {/* Stepper */}
                  <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>

                  {/* Error Alert */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.2rem',
                        },
                      }}
                      onClose={() => setError('')}
                    >
                      {error}
                    </Alert>
                  )}

                  {/* Success Alert */}
                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.2rem',
                        },
                      }}
                      onClose={() => setSuccess('')}
                    >
                      {success}
                    </Alert>
                  )}

                  {/* Step 1: Enter Details Form */}
                  {activeStep === 0 && (
                    <form onSubmit={handleStep1Submit(onSendOTP)}>
                     

                      <Box sx={{ mb: 3 }}>
                        <Controller
                          name="username"
                          control={step1Control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Username"
                              variant="outlined"
                              error={!!step1Errors.username}
                              helperText={step1Errors.username?.message}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  },
                                  '&.Mui-focused': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                  },
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AccountIcon sx={{ color: '#1e3c72' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                       <Box sx={{ mb: 3 }}>
                        <Controller
                          name="email"
                          control={step1Control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Email Address"
                              variant="outlined"
                              type="email"
                              placeholder="example@domain.com"
                              error={!!step1Errors.email}
                              helperText={step1Errors.email?.message || 'Enter your registered email address'}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  },
                                  '&.Mui-focused': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                  },
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon sx={{ color: '#1e3c72' }} />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box sx={{ mb: 4 }}>
                        <Controller
                          name="mobile"
                          control={step1Control}
                          render={({ field: { onChange, value, ...field } }) => (
                            <TextField
                              {...field}
                              value={value}
                              onChange={(e) => {
                                // Only allow numbers and limit to 10 digits
                                const input = e.target.value.replace(/\D/g, '').slice(0, 10);
                                onChange(input);
                              }}
                              fullWidth
                              label="Mobile Number"
                              variant="outlined"
                              placeholder="Enter 10-digit mobile number"
                              error={!!step1Errors.mobile}
                              helperText={step1Errors.mobile?.message || 'Enter Indian mobile number (10 digits)'}
                              inputProps={{
                                maxLength: 10,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                  },
                                  '&.Mui-focused': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                  },
                                },
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon sx={{ color: '#1e3c72' }} />
                                    <Typography variant="body2" sx={{ ml: 1, color: '#666' }}>
                                      +91
                                    </Typography>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          mb: 2,
                          py: 1.5,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                          boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                          },
                        }}
                      >
                        <LoaderWrapper loading={loading} type="button">
                          {loading ? 'Sending OTP...' : 'Send OTP'}
                        </LoaderWrapper>
                      </Button>
                    </form>
                  )}

                  {/* Step 2: Verify OTP Form */}
                  {activeStep === 1 && (
                    <Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                        sx={{ mb: 3 }}
                      >
                        Please enter the 6-digit OTP sent to your registered email.
                      </Typography>

                      <form onSubmit={handleStep2Submit(onVerifyOTP)}>
                        <Box sx={{ mb: 4 }}>
                          <Controller
                            name="otp"
                            control={step2Control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                label="Enter OTP"
                                variant="outlined"
                                error={!!step2Errors.otp}
                                helperText={step2Errors.otp?.message}
                                inputProps={{
                                  maxLength: 6,
                                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                    '&.Mui-focused': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={loading}
                          sx={{
                            mb: 2,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #3b82f6 100%)',
                            boxShadow: '0 4px 15px rgba(30, 60, 114, 0.4)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(30, 60, 114, 0.6)',
                            },
                            '&:disabled': {
                              background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                            },
                          }}
                        >
                          <LoaderWrapper loading={loading} type="button">
                            {loading ? 'Verifying...' : 'Verify OTP'}
                          </LoaderWrapper>
                        </Button>

                        <Button
                          fullWidth
                          variant="outlined"
                          size="large"
                          onClick={onResendOTP}
                          disabled={loading}
                          sx={{
                            mb: 2,
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            borderColor: '#1e3c72',
                            color: '#1e3c72',
                            '&:hover': {
                              borderColor: '#2a5298',
                              backgroundColor: '#e3f2fd',
                            },
                          }}
                        >
                          Resend OTP
                        </Button>

                        <Button
                          fullWidth
                          variant="text"
                          startIcon={<ArrowBackIcon />}
                          onClick={() => {
                            setActiveStep(0);
                            setError('');
                            setSuccess('');
                          }}
                          sx={{
                            color: '#1e3c72',
                            '&:hover': {
                              backgroundColor: '#e3f2fd',
                            },
                          }}
                        >
                          Back to Enter Details
                        </Button>
                      </form>
                    </Box>
                  )}

                  <Divider sx={{ my: 3, '&::before, &::after': { borderColor: 'rgba(0,0,0,0.1)' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Remember your password?
                    </Typography>
                  </Divider>

                  <Box sx={{ textAlign: 'center' }}>
                    <Link
                      component={RouterLink}
                      to="/main/login"
                      variant="body2"
                      sx={{
                        display: 'inline-block',
                        p: 1,
                        borderRadius: 1,
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        color: '#1e3c72',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                          color: '#1e3c72',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Back to Login
                    </Link>
                  </Box>
                </Box>
              </Slide>
            </CardContent>
          </Paper>
        </Fade>
      </Container>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Box>
  );
};

export default ForgotPasswordPage;
